import { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { Plus, X, Pencil, ChevronsUpDown, Check } from 'lucide-react';
import { toast } from '../../hooks/useToast';
import { getErrorMessage } from '../../lib/api';
import { productService } from '../../services/product.service';
import { manufacturerService } from '../../services/manufacturer.service';
import type { Product, ProductInput } from '../../types/crm';
import type { Manufacturer, PaginationMeta } from '../../types';
import Loading from '../../components/Loading';

const productSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  category: z.string().min(1, 'Category is required'),
  sku: z.string().optional(),
  description: z.string().optional(),
  unit: z.string().min(1, 'Unit is required'),
  pack_size: z.string().optional(),
  list_price: z.string().optional(),
  cost: z.string().optional(),
  manufacturer_id: z.number().nullable().optional(),
  active: z.boolean().optional(),
});

type ProductFormData = z.infer<typeof productSchema>;

const DEFAULT_FORM: ProductFormData = {
  name: '',
  category: '',
  sku: '',
  description: '',
  unit: 'case',
  pack_size: '',
  list_price: '',
  cost: '',
  manufacturer_id: null,
  active: true,
};

// Strip empty strings out of optional fields so the API stores null instead of
// "" for SKU, pack_size, etc.
const toInput = (data: ProductFormData): ProductInput => ({
  name: data.name,
  category: data.category,
  sku: data.sku?.trim() ? data.sku.trim() : null,
  description: data.description?.trim() ? data.description.trim() : null,
  unit: data.unit,
  pack_size: data.pack_size?.trim() ? data.pack_size.trim() : null,
  list_price: data.list_price?.trim() ? data.list_price.trim() : null,
  cost: data.cost?.trim() ? data.cost.trim() : null,
  manufacturer_id: data.manufacturer_id ?? null,
  active: data.active ?? true,
});

export const Products = () => {
  const [items, setItems] = useState<Product[]>([]);
  const [, setMeta] = useState<PaginationMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [manufacturerFilter, setManufacturerFilter] = useState<number | null>(null);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: DEFAULT_FORM,
  });

  const selectedManufacturerId = watch('manufacturer_id');
  const selectedActive = watch('active');

  const loadProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const q: Record<string, unknown> = {};
      if (search.trim()) q['sku_cont'] = search.trim();
      if (categoryFilter.trim()) q['category_eq'] = categoryFilter.trim();
      if (manufacturerFilter) q['manufacturer_id_eq'] = manufacturerFilter;

      const result = await productService.getAll({ q });
      setItems(result.data);
      setMeta(result.meta);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [search, categoryFilter, manufacturerFilter]);

  const loadManufacturers = useCallback(async () => {
    try {
      // Load all manufacturers for the picker. We cap at a generous limit
      // and trust the small N for now; if it grows, swap to an async search.
      const result = await manufacturerService.list({ per_page: 200 });
      setManufacturers(result.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadManufacturers();
  }, [loadManufacturers]);

  const openCreate = () => {
    setEditing(null);
    reset(DEFAULT_FORM);
    setShowModal(true);
  };

  const openEdit = (product: Product) => {
    setEditing(product);
    reset({
      name: product.name,
      category: product.category,
      sku: product.sku ?? '',
      description: product.description ?? '',
      unit: product.unit,
      pack_size: product.pack_size ?? '',
      list_price: product.list_price ?? '',
      cost: product.cost ?? '',
      manufacturer_id: product.manufacturer?.id ?? null,
      active: product.active,
    });
    setShowModal(true);
  };

  const onSubmit = async (data: ProductFormData) => {
    setIsSaving(true);
    try {
      const payload = toInput(data);
      if (editing) {
        await productService.update(editing.id, payload);
        toast.success('Product updated');
      } else {
        await productService.create(payload);
        toast.success('Product created');
      }
      setShowModal(false);
      reset(DEFAULT_FORM);
      loadProducts();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (product: Product) => {
    try {
      const updated = await productService.update(product.id, { active: !product.active });
      setItems((prev) => prev.map((p) => (p.id === product.id ? updated : p)));
      toast.success(`Product ${updated.active ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const categories = useMemo(() => {
    const set = new Set(items.map((p) => p.category).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  const selectedManufacturer = useMemo(
    () => manufacturers.find((m) => m.id === selectedManufacturerId) ?? null,
    [manufacturers, selectedManufacturerId]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="text-sm text-gray-600 mt-1">
            Global product catalog
          </p>
        </div>
        <button onClick={openCreate} className="btn btn-primary">
          <Plus className="h-4 w-4 inline mr-2" />
          Add Product
        </button>
      </div>

      {/* Filters */}
      <div className="card grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="label">SKU search</label>
          <input
            className="input"
            placeholder="Search by SKU…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Category</label>
          <select
            className="input"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Manufacturer</label>
          <select
            className="input"
            value={manufacturerFilter ?? ''}
            onChange={(e) =>
              setManufacturerFilter(e.target.value ? Number(e.target.value) : null)
            }
          >
            <option value="">All manufacturers</option>
            {manufacturers.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <Loading />
      ) : items.length === 0 ? (
        <div className="card text-center text-gray-500 py-12">
          No products found.
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit</th>
                  <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">List Price</th>
                  <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manufacturer</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                  <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {product.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {product.sku ?? '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {product.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {product.unit}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                      {product.list_price ?? '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                      {product.cost ?? '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {product.manufacturer?.name ?? '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          product.active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {product.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                      <button
                        onClick={() => openEdit(product)}
                        className="text-blue-600 hover:text-blue-900"
                        aria-label={`Edit ${product.name}`}
                      >
                        <Pencil className="h-4 w-4 inline" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(product)}
                        className={product.active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}
                      >
                        {product.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-(--color-bg-scrim)"
              onClick={() => setShowModal(false)}
            />

            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {editing ? 'Edit Product' : 'Add Product'}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="label">Name</label>
                      <input {...register('name')} id="name" type="text" className="input" />
                      {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="category" className="label">Category</label>
                        <input {...register('category')} id="category" type="text" className="input" />
                        {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>}
                      </div>
                      <div>
                        <label htmlFor="sku" className="label">SKU</label>
                        <input {...register('sku')} id="sku" type="text" className="input" />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="unit" className="label">Unit</label>
                        <input {...register('unit')} id="unit" type="text" className="input" placeholder="case" />
                        {errors.unit && <p className="mt-1 text-sm text-red-600">{errors.unit.message}</p>}
                      </div>
                      <div>
                        <label htmlFor="pack_size" className="label">Pack size</label>
                        <input {...register('pack_size')} id="pack_size" type="text" className="input" placeholder="6/#10" />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="list_price" className="label">List price</label>
                        <input {...register('list_price')} id="list_price" type="text" inputMode="decimal" className="input" placeholder="0.00" />
                      </div>
                      <div>
                        <label htmlFor="cost" className="label">Cost</label>
                        <input {...register('cost')} id="cost" type="text" inputMode="decimal" className="input" placeholder="0.00" />
                      </div>
                    </div>

                    <div>
                      <label className="label">Manufacturer</label>
                      <ManufacturerPicker
                        manufacturers={manufacturers}
                        value={selectedManufacturer}
                        onChange={(m) => setValue('manufacturer_id', m?.id ?? null)}
                      />
                    </div>

                    <div>
                      <label htmlFor="description" className="label">Description</label>
                      <textarea
                        {...register('description')}
                        id="description"
                        rows={3}
                        className="input"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        id="active"
                        type="checkbox"
                        checked={selectedActive ?? true}
                        onChange={(e) => setValue('active', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <label htmlFor="active" className="text-sm text-gray-700">Active</label>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                  <button type="submit" disabled={isSaving} className="btn btn-primary">
                    {isSaving ? 'Saving…' : editing ? 'Save Changes' : 'Create Product'}
                  </button>
                  <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ----- Manufacturer picker (Headless UI Combobox, single-select) -----

interface ManufacturerPickerProps {
  manufacturers: Manufacturer[];
  value: Manufacturer | null;
  onChange: (next: Manufacturer | null) => void;
}

const ManufacturerPicker = ({ manufacturers, value, onChange }: ManufacturerPickerProps) => {
  const [query, setQuery] = useState('');

  const filtered =
    query === ''
      ? manufacturers
      : manufacturers.filter((m) =>
          m.name.toLowerCase().includes(query.toLowerCase())
        );

  return (
    <Combobox value={value} onChange={(next: Manufacturer | null) => onChange(next)}>
      <div className="relative">
        <div className="input flex items-center gap-1">
          <ComboboxInput
            className="flex-1 border-none outline-none p-0 text-sm bg-transparent"
            displayValue={(m: Manufacturer | null) => m?.name ?? ''}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Pick a manufacturer (optional)"
          />
          {value && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onChange(null);
                setQuery('');
              }}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Clear manufacturer"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <ComboboxButton className="text-gray-400">
            <ChevronsUpDown className="h-4 w-4" />
          </ComboboxButton>
        </div>
        <ComboboxOptions className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-gray-500">No manufacturers found</div>
          ) : (
            filtered.map((m) => (
              <ComboboxOption
                key={m.id}
                value={m}
                className={({ focus }) =>
                  `relative cursor-default select-none py-2 pl-9 pr-3 ${
                    focus ? 'bg-blue-600 text-white' : 'text-gray-900'
                  }`
                }
              >
                {({ focus, selected }) => (
                  <>
                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                      {m.name}
                    </span>
                    {selected && (
                      <span
                        className={`absolute inset-y-0 left-0 flex items-center pl-2 ${
                          focus ? 'text-white' : 'text-blue-600'
                        }`}
                      >
                        <Check className="h-4 w-4" />
                      </span>
                    )}
                  </>
                )}
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
};

export default Products;
